/*
 *  Copyright 2019-2023  SenX S.A.S.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 *
 */

import {Component, h, Method, Prop} from '@stencil/core';
import 'cookieconsent';
declare global {
  interface Window { gtag: any; dataLayer: any[], gaAvailable: boolean}
}
@Component({
  tag: 'senx-ga',
  styleUrls: ['ga.scss'],
  shadow: false
})
export class Ga {
  @Prop() trackingCode: string;
  @Prop() name: string = 'senx';
  @Prop() policyUrl: string;
  @Prop() position: string = 'top';

  @Method()
  async sendEvent(action: string = '', category: string = '', label: string = '', value: boolean = true) {
    if (!!window.gaAvailable) {
      window.gtag('event', action, {
        'event_category': category,
        'event_label': label,
        value
      });
    }
  }

  // noinspection JSUnusedGlobalSymbols
  async componentDidLoad() {
    const me = this;

    const conf = {
      cookie: {
        name: this.name
      },
      palette: {
        popup: {
          background: "#000000"
        },
        button: {
          background: "#ff9900"
        }
      },
      content: {
        href: this.policyUrl || ''
      },
      showLink: !!this.policyUrl,
      dismissOnWindowClick: true,
      autoOpen: true,
      position: this.position,
      type: "opt-out",
      onPopupOpen: function (status) {
        const didConsent = this.hasConsented();
        console.debug('onPopupOpen', 'didConsent', didConsent, status);
        me.enableGA(didConsent || !status);
      },
      onInitialise: function (status) {
        const didConsent = this.hasConsented();
        console.debug('onInitialise', 'didConsent', didConsent, status);
        me.enableGA(didConsent || !status);
      },
      onStatusChange: function () {
        console.debug(this.hasConsented() ? 'enable cookies' : 'disable cookies');
        me.enableGA(this.hasConsented());
      },
      law: {
        regionalLaw: true,
      },
      location: {
        serviceDefinitions: {
          locationService: () => {
            return {
              url: 'https://reallyfreegeoip.org/json/',
              // @ts-ignore
              callback: function (done, response) {
                try {
                  const json = JSON.parse(response);
                  console.debug(json)
                  if (json.country_code) {
                    return {code: json.country_code}
                  }
                } catch (err) {
                  return new Error('Invalid response (' + err + ')');
                }
              },
            };
          },
        },
        services: [
          'locationService'
        ]
      }
    }
    try {
      await fetch('https://reallyfreegeoip.org/json/');
    } catch(e) {
      conf.law.regionalLaw = false;
      conf.location = undefined;
    }
    window['cookieconsent'].initialise(conf);
  }

  private loadScript(script: string, async = true, type = "text/javascript") {
    return new Promise((resolve, reject) => {
      if (!document.getElementById('senx-ga-script')) {
        try {
          const scriptEle = document.createElement("script");
          scriptEle.type = type;
          scriptEle.async = async;
          scriptEle.src = script;
          scriptEle.id = 'senx-ga-script';
          scriptEle.addEventListener("load", () => resolve({status: true}));
          scriptEle.addEventListener("error", () => reject({
            status: false,
            message: `Failed to load the script ${script}`
          }));
          document.head.appendChild(scriptEle);
        } catch(error) {
          reject(error);
        }
      }
    });
  };

  private enableGA(didConsent) {
    console.debug('enableGA', !!this.trackingCode, didConsent);
    if (!!this.trackingCode && didConsent) {
      this.loadScript('https://www.googletagmanager.com/gtag/js?id=' + this.trackingCode).then(() => {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {window.dataLayer.push(arguments)};
        window.gtag('js', new Date());
        window.gtag('config', this.trackingCode);
        window.gaAvailable = true;
        console.debug('ga loaded');
      });
    }
    if (!didConsent) {
      window['ga-disable-' + this.trackingCode] = true;
    }
  }

  render() {
    return <div/>;
  }
}
